import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react'
import { Link, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { BookOpen, LayoutGrid, Lock, ShoppingBag, Store } from 'lucide-react'
import { APP_ICON_REGISTRY, NAV_ITEM_BY_ID } from '../../constants/appSuites'
import { useActiveApp } from '../../contexts/ActiveAppContext'
import { useAppSuiteSettings } from '../../contexts/AppSuiteSettingsContext'
import { useNavVisibility } from '../../hooks/useNavVisibility'
import type { AppIconKey, LauncherAppId } from '../../types/appSuites'
import { isAgentStorePath, isWorkflowStorePath } from '../../utils/appSuites/storePaths'
import './AppSwitcher.css'

const HEX = /^#[0-9a-fA-F]{6}$/
/** Synced to AppSidebar + App.css — blocks nav-rail hover expand while the menu is open. */
export const APP_SWITCHER_OPEN_BODY_CLASS = 'app-switcher-open'

function switcherItemClass(active: boolean): string {
  return `app-switcher__item${active ? ' app-switcher__item--active' : ''}`
}

export function AppSwitcher() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const menuId = useId()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const { apps, activeApp, accessibleApps, lockedApps, switchApp, goToLauncher } = useActiveApp()
  const { suite } = useAppSuiteSettings()
  const { isItemVisible } = useNavVisibility()
  const hideLocked = suite?.hide_locked_apps ?? false
  const shown = hideLocked ? accessibleApps : apps

  const close = useCallback(() => {
    setOpen(false)
    buttonRef.current?.focus()
  }, [])

  useEffect(() => {
    document.body.classList.toggle(APP_SWITCHER_OPEN_BODY_CLASS, open)
    return () => document.body.classList.remove(APP_SWITCHER_OPEN_BODY_CLASS)
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [close, open])

  const onMenuKey = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    const items = menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])')
    if (!items || items.length === 0) return
    const list = Array.from(items)
    const index = list.indexOf(document.activeElement as HTMLButtonElement)
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      list[(index + 1) % list.length]?.focus()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      list[(index - 1 + list.length) % list.length]?.focus()
    }
  }, [])

  const openApp = useCallback((appId: LauncherAppId) => {
    switchApp(appId)
    setOpen(false)
  }, [switchApp])

  const openLauncher = useCallback(() => {
    goToLauncher()
    setOpen(false)
  }, [goToLauncher])

  const toggleOpen = useCallback(() => {
    setOpen((value) => !value)
  }, [])

  const onAppClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    const appId = event.currentTarget.dataset.appId
    if (
      appId === 'context' ||
      appId === 'ideate' ||
      appId === 'studio' ||
      appId === 'observability' ||
      appId === 'admin'
    ) {
      openApp(appId)
    }
  }, [openApp])

  const storeVisible = isItemVisible(NAV_ITEM_BY_ID.agent_store)
  const workflowVisible = isItemVisible(NAV_ITEM_BY_ID.workflow_store)
  const docsVisible = isItemVisible(NAV_ITEM_BY_ID.docs)
  const onLauncher = pathname === '/'
  const onAgentStore = isAgentStorePath(pathname)
  const onWorkflowStore = isWorkflowStorePath(pathname)

  return (
    <div className="app-switcher">
      <button
        ref={buttonRef}
        type="button"
        className="app-switcher__btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={t('appSuites.switcher.label')}
        onClick={toggleOpen}
      >
        <LayoutGrid size={16} />
      </button>
      {open && (
        <div
          ref={menuRef}
          id={menuId}
          className="app-switcher__menu"
          role="menu"
          onKeyDown={onMenuKey}
        >
          {shown.map((app) => {
            const Icon = APP_ICON_REGISTRY[app.icon as AppIconKey]
            const locked = lockedApps.some((entry) => entry.id === app.id)
            const accent = HEX.test(app.accent_color) ? app.accent_color : 'var(--primary)'
            const isActiveApp = activeApp?.id === app.id
            return (
              <button
                key={app.id}
                type="button"
                role="menuitem"
                className={switcherItemClass(isActiveApp)}
                disabled={locked}
                data-app-id={app.id}
                onClick={onAppClick}
              >
                <span className="app-switcher__icon" style={{ background: accent }}>
                  {Icon ? <Icon size={14} /> : null}
                </span>
                <span>{app.name}</span>
                {locked && <Lock size={12} aria-hidden="true" />}
              </button>
            )
          })}
          <div className="app-switcher__divider" role="separator" />
          <button
            type="button"
            role="menuitem"
            className={switcherItemClass(onLauncher)}
            onClick={openLauncher}
          >
            <LayoutGrid size={14} />
            <span>{t('appSuites.switcher.allApps')}</span>
          </button>
          {storeVisible && (
            <Link
              className={switcherItemClass(onAgentStore)}
              role="menuitem"
              to="/agent-store"
              onClick={close}
            >
              <Store size={14} />
              <span>{t('nav.agentStore')}</span>
            </Link>
          )}
          {workflowVisible && (
            <Link
              className={switcherItemClass(onWorkflowStore)}
              role="menuitem"
              to="/workflow-store"
              onClick={close}
            >
              <ShoppingBag size={14} />
              <span>{t('nav.workflowStore')}</span>
            </Link>
          )}
          {docsVisible && (
            <a className="app-switcher__item" role="menuitem" href="/product_docs/" target="_blank" rel="noreferrer">
              <BookOpen size={14} />
              <span>{t('nav.docs')}</span>
            </a>
          )}
        </div>
      )}
    </div>
  )
}
